import PropTypes from 'prop-types';
import React from 'react';
import { noop } from 'lodash';
import styled from 'styled-components';

import { Box, Flex } from '../../grid';
import { Icon } from '../../icons';
import { Image } from '../../image';
import { P } from '../../typography/new';

// VI-redesigned offer choice selector — scoped to the coupon / offer "Promotion details" modal.
// Differs from the shared StylePicker/InputListItem: title uses Barlow (P medium-semi-bold) instead of
// the brand-font Subheading, rows use a filled tick indicator + Barlow-semibold label (not the brand-font
// H6), and an optional 64px rounded product thumbnail. Building this separately keeps the ~15 other
// InputListItem consumers untouched.

const Row = styled(Flex)`
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  border-top: 1px solid ${({ theme }) => theme.colors.warmWhite3};
  border-bottom: 1px solid ${({ theme }) => theme.colors.warmWhite3};

  & + & {
    border-top: 0;
  }
`;

// A row's height is content-driven, so the 64px thumbnail is what sets it on a product
// choice. "Apply offer to item in order" carries no thumbnail, so its row collapsed to the
// label's line height and read as though it had less vertical padding than its neighbours
// — the py is identical on every row. Reserve the thumbnail's height instead, but only when
// the list actually has thumbnails, so an all-text offer keeps its compact rows.
const THUMBNAIL_SIZE = 64;
const ROW_PADDING_Y = 14;
const ROW_MIN_HEIGHT = `${THUMBNAIL_SIZE + ROW_PADDING_Y * 2}px`;

const UnselectedIndicator = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.black};
`;

const OfferChoiceList = ({ title, required, hasError, items, selectedItems, onSelect }) => {
  const hasThumbnails = items.some(item => item.image);

  return (
    <Box width="100%">
      <Flex justifyContent="space-between" alignItems="flex-end" pb={{ xs: '20px', lg: '24px' }}>
        <P variant="medium-semi-bold" color="black">
          {title}
        </P>
        {required && (
          <P variant="small" color={hasError ? 'redPrimary' : 'greyPrimary'} textAlign="right">
            Required
          </P>
        )}
      </Flex>

      <Flex flexDirection="column" data-testid="offer-choice-list">
        {items.map(item => {
          const selected = selectedItems.some(value => value === item.value);

          return (
            <Row
              key={item.value}
              disabled={item.disabled}
              alignItems="center"
              justifyContent="space-between"
              py={`${ROW_PADDING_Y}px`}
              minHeight={hasThumbnails ? ROW_MIN_HEIGHT : undefined}
              onClick={item.disabled ? noop : () => onSelect([item])}
            >
              <Flex alignItems="flex-start" flex="1 1 0" minWidth={0} mr="16px">
                {/* @NOTE: "tickCircle", not "tick-circle". The icon map is keyed camelCase, so the
                  hyphenated name resolved to undefined and the selected indicator rendered as
                  nothing at all — leaving no way to see which choice was picked. */}
                {selected ? <Icon name="tickCircle" fill="black" flexShrink={0} mt="2px" /> : <UnselectedIndicator />}

                <Flex flexDirection="column" justifyContent="center" flex="1 1 0" minWidth={0} ml="16px">
                  <P variant="standard-semi-bold" color="black">
                    {item.label}
                  </P>
                  {item.disabled && (
                    <P variant="small" color="greyPrimary">
                      Sold Out
                    </P>
                  )}
                </Flex>
              </Flex>

              {item.image && (
                <Box
                  flexShrink={0}
                  width={`${THUMBNAIL_SIZE}px`}
                  height={`${THUMBNAIL_SIZE}px`}
                  borderRadius="8px"
                  overflow="hidden"
                  ml="16px"
                >
                  {/* Decorative: the row's label already names the product. */}
                  <Image src={item.image} alt="" width="100%" height="100%" objectFit="cover" focalPoint={[0.5, 0.5]} />
                </Box>
              )}
            </Row>
          );
        })}
      </Flex>
    </Box>
  );
};

OfferChoiceList.propTypes = {
  title: PropTypes.string.isRequired,
  required: PropTypes.bool,
  hasError: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string.isRequired,
      image: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      disabled: PropTypes.bool,
    })
  ).isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelect: PropTypes.func.isRequired,
};

OfferChoiceList.defaultProps = {
  required: false,
  hasError: false,
};

export { OfferChoiceList };
